import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import WpaLayout from './WpaLayout';
import { wpaCommerceHeaders, wpaLocalizedName } from './wpaCommerceFetch';
import { sortWpaCollections } from './wpaCollectionSort';

const PRIMARY = '#4CAF50';

const styles = {
  list: {
    padding: '8px 0 80px',
  },
  categoryRow: {
    display: 'flex',
    alignItems: 'center',
    padding: '16px 20px',
    borderBottom: '1px solid #f0f0f0',
    cursor: 'pointer',
    gap: 12,
    background: '#fff',
  },
  categoryIcon: {
    width: 44,
    height: 44,
    borderRadius: 10,
    background: '#e8f5e9',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 22,
    flexShrink: 0,
  },
  categoryInfo: {
    flex: 1,
    minWidth: 0,
  },
  categoryName: {
    fontSize: 16,
    fontWeight: 500,
    color: '#333',
  },
  categoryCount: {
    fontSize: 13,
    color: '#999',
    marginTop: 2,
  },
  chevron: {
    fontSize: 18,
    color: '#ccc',
    flexShrink: 0,
  },
  bottomBar: {
    position: 'fixed',
    bottom: 0,
    left: '50%',
    transform: 'translateX(-50%)',
    width: '100%',
    maxWidth: 480,
    padding: '12px 16px',
    background: '#fff',
    borderTop: '1px solid #e0e0e0',
    zIndex: 900,
  },
  bottomBtn: {
    width: '100%',
    padding: '14px 0',
    background: PRIMARY,
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    fontSize: 16,
    fontWeight: 600,
    cursor: 'pointer',
  },
  loading: {
    textAlign: 'center',
    padding: 32,
    color: '#999',
  },
};

export default function WpaCategoriesPage() {
  const { t, currentLanguage } = useLanguage();
  const navigate = useNavigate();
  const { companyId } = useParams();
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchCollections = useCallback(async () => {
    const token = localStorage.getItem('wpa_token');
    try {
      const res = await fetch('/api/wpa-commerce/collections', {
        headers: wpaCommerceHeaders(token, currentLanguage),
      });
      if (res.status === 401) {
        navigate(`/${companyId}/wpa/login`, { replace: true });
        return;
      }
      const json = await res.json();
      const raw = json.data || json.items || json;
      setCollections(sortWpaCollections(Array.isArray(raw) ? raw : []));
    } catch (err) {
      console.error('Failed to fetch collections', err);
    } finally {
      setLoading(false);
    }
  }, [navigate, companyId, currentLanguage]);

  useEffect(() => {
    fetchCollections();
  }, [fetchCollections]);

  return (
    <WpaLayout
      title={t('wpa.titles.categories')}
      leadingMode="back"
      onBack={() => navigate(`/${companyId}/wpa/home`)}
    >
      <div style={styles.list}>
        {loading && <div style={styles.loading}>{t('wpa.common.loading')}</div>}
        {!loading && collections.length === 0 && (
          <div style={styles.loading}>{t('wpa.common.noCategories')}</div>
        )}
        {collections.map((c) => (
          <div
            key={c.id}
            style={styles.categoryRow}
            onClick={() => navigate(`/${companyId}/wpa/products?collectionId=${c.id}`)}
          >
            <div style={styles.categoryIcon}>📦</div>
            <div style={styles.categoryInfo}>
              <div style={styles.categoryName}>{wpaLocalizedName(c)}</div>
              {c.productCount != null && (
                <div style={styles.categoryCount}>{t('wpa.categories.itemsCount', { count: c.productCount })}</div>
              )}
            </div>
            <span style={styles.chevron}>›</span>
          </div>
        ))}
      </div>

      <div style={styles.bottomBar}>
        <button type="button" style={styles.bottomBtn} onClick={() => navigate(`/${companyId}/wpa/home`)}>
          {t('wpa.categories.featuredBtn')}
        </button>
      </div>
    </WpaLayout>
  );
}
